import { type JSX, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import authService from '../services/authService';
import { CaptchaRequiredError, emailErrorLog } from '../services/errorLogger';
import { aosphereURL, REACT_APP_GOOGLE_RECAPTCHA_ENTERPRISE_SITE_KEY_FOR_ERROR_EMAIL } from '../util/constants';
import {
  getEnterpriseCaptchaToken,
  resetEnterpriseCaptcha,
  useEnterpriseRecaptchaScript,
} from '../util/enterpriseRecaptcha.util';
import { logger } from '../util/logger';
import { CheckIcon, CopyIcon } from './icons';
import './errorPage.css';

const COPIED_LABEL_DURATION_MS = 1500;
const RECAPTCHA_ACTION = 'email_error_details';
const DIALOG_TITLE = 'Send error details to support?';
const DIALOG_DESCRIPTION =
  "Something went wrong loading this page. Sharing the details below helps our support team investigate and fix it faster.";
const WHAT_WE_SEND_ITEMS = [
  'Browser type and version',
  'The page you were on and the error message',
  'Console error logs from this session',
  'Date and time of the error',
];
const COMMENT_LABEL = "Anything else you'd like to add?";
const COMMENT_PLACEHOLDER = 'e.g. what you were doing when this happened';
const CONSENT_LABEL = 'I agree to send this information to aosphere support';
const GENERIC_SEND_FAILURE_MESSAGE = 'Failed to send error details. Please try again.';
const COMPLETE_CAPTCHA_MESSAGE = 'Please complete the CAPTCHA verification.';
/** Shown when the isLoggedIn cookie said "authenticated" but the actual access token
 * had expired — emailErrorLog's first (no-captcha) attempt came back 401 and refused
 * to fall back to the public endpoint without one. The widget appears right below
 * this message once it's set. */
const SESSION_EXPIRED_CAPTCHA_MESSAGE =
  'Your session appears to have expired. Please complete the CAPTCHA below, then press Send again.';
const CONSENT_REQUIRED_MESSAGE = `Check "${CONSENT_LABEL}" to enable sending.`;
const PRIVACY_STATEMENT_URL = `${aosphereURL}/privacy-statement/`;

function openPrivacyNotice(): void {
  window.open(PRIVACY_STATEMENT_URL, '_blank', 'noopener,noreferrer');
}

// Recognized as a whole word and kept fully uppercase instead of Title Case —
// e.g. "correlationId" -> "Correlation ID", "pageUrl" -> "Page URL".
const KEY_ACRONYMS = new Set([
  'ID',
  'URL',
  'API',
  'OS',
  'UI',
  'IP',
  'JWT',
  'HTML',
  'JSON',
  'UTC',
  'SSO',
  'OTP',
  'UUID',
  'CSS',
  'DOM',
  'PDF',
  'URLS',
]);

/** "correlationId" -> "Correlation ID", "systemOSVersion" -> "System OS Version". */
function humanizeKey(key: string): string {
  const words = key
    .replaceAll(/([\da-z])([A-Z])/g, '$1 $2')
    .replaceAll(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .filter(Boolean);

  return words
    .map((word) => {
      const upper = word.toUpperCase();
      return KEY_ACRONYMS.has(upper) ? upper : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/** Recursively rewrites every object key in a parsed JSON value to its human-readable
 * label — the support team reads the emailed payload as prose, not raw camelCase field names. */
function humanizeJsonKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => humanizeJsonKeys(item));
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, value_]) => [humanizeKey(key), humanizeJsonKeys(value_)])
    );
  }
  return value;
}

/** Adds the optional free-text comment to the JSON body as `miscInfoByUser`, then
 * humanizes every key (see `humanizeJsonKeys`) — this is the body as it will actually
 * be emailed. Falls back to the original body untouched if it isn't valid JSON. */
function withMiscInfoByUser(body: string, comment: string): string {
  try {
    const parsed = JSON.parse(body) as Record<string, unknown>;
    return JSON.stringify(humanizeJsonKeys({ ...parsed, miscInfoByUser: comment.trim() }), null, 2);
  } catch {
    return body;
  }
}

export interface EmailErrorDialogProps {
  readonly show: boolean;
  readonly correlationId: string;
  /** Pre-built subject/body for the email — see ErrorStateCard's emailSubject/emailBody. */
  readonly subject: string;
  readonly body: string;
  readonly onHide: () => void;
  /** Called once emailErrorLog succeeds, before the dialog closes. */
  readonly onSent: () => void;
}

/**
 * Confirmation dialog for the "email this error" affordance on ErrorStateCard —
 * requires explicit consent and a reCAPTCHA pass before calling emailErrorLog.
 * Mounted only while `show` is true, so captcha state resets for free on next open.
 */
export function EmailErrorDialog({
  show,
  correlationId,
  subject,
  body,
  onHide,
  onSent,
}: EmailErrorDialogProps): JSX.Element {
  const [hasConsented, setHasConsented] = useState(false);
  const [comment, setComment] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [justCopied, setJustCopied] = useState(false);
  // Set only after an authenticated-looking send actually comes back 401 mid-flight
  // (see sendWithCaptcha's CaptchaRequiredError handling) — forces the captcha step
  // even though isUserAuthenticated said it wasn't needed.
  const [captchaRequired, setCaptchaRequired] = useState(false);
  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useRef<T>() overload requires the argument
  const revertCopiedRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Cookie-based, synchronous — same source of truth errorLogger.ts uses to pick
  // between the authenticated and public `/errors/email` endpoints. This cookie can
  // outlive the actual access token, so it's an optimistic first guess, not a
  // guarantee — captchaRequired is the correction once emailErrorLog finds out the
  // hard way (a 401) that the public, captcha-mandatory endpoint is needed after all.
  const isUserAuthenticated = authService.isAuthenticated();
  const isCaptchaVisible = !isUserAuthenticated || captchaRequired;

  useEffect(() => {
    return () => clearTimeout(revertCopiedRef.current);
  }, []);

  function handleCopyReferenceId(): void {
    void navigator.clipboard.writeText(correlationId);
    setJustCopied(true);
    clearTimeout(revertCopiedRef.current);
    revertCopiedRef.current = setTimeout(() => setJustCopied(false), COPIED_LABEL_DURATION_MS);
  }

  useEnterpriseRecaptchaScript(
    show && isCaptchaVisible && !!REACT_APP_GOOGLE_RECAPTCHA_ENTERPRISE_SITE_KEY_FOR_ERROR_EMAIL
  );

  async function sendWithCaptcha(captcha?: { token: string; action: string }): Promise<void> {
    setIsSending(true);
    setDialogError('');

    try {
      await emailErrorLog({
        subject,
        body: withMiscInfoByUser(body, comment),
        captcha,
      });
      onSent();
      onHide();
    } catch (error) {
      if (error instanceof CaptchaRequiredError) {
        // The isLoggedIn cookie said authenticated, but /errors/email just came back
        // 401 (an actually-expired access token) — emailErrorLog refused to fall back
        // to /public/errors/email without a captcha, since that endpoint requires one
        // unconditionally. Reveal the widget; the next Send click (once completed)
        // retries with a real token.
        setCaptchaRequired(true);
        setDialogError(SESSION_EXPIRED_CAPTCHA_MESSAGE);
        return;
      }
      logger.error('Failed to email error details', error);
      setDialogError(GENERIC_SEND_FAILURE_MESSAGE);
      if (captcha) {
        resetEnterpriseCaptcha();
      }
    } finally {
      setIsSending(false);
    }
  }

  function handleSendClick(): void {
    if (!hasConsented || isSending) {
      return;
    }

    if (!isCaptchaVisible) {
      void sendWithCaptcha();
      return;
    }

    if (!REACT_APP_GOOGLE_RECAPTCHA_ENTERPRISE_SITE_KEY_FOR_ERROR_EMAIL) {
      setDialogError('reCAPTCHA is not configured. Please contact support.');
      return;
    }

    const token = getEnterpriseCaptchaToken();
    if (!token) {
      setDialogError(COMPLETE_CAPTCHA_MESSAGE);
      return;
    }

    setDialogError('');
    void sendWithCaptcha({ token, action: RECAPTCHA_ACTION });
  }

  const isSendDisabled = !hasConsented || isSending;
  const sendTooltipMessage = hasConsented ? undefined : CONSENT_REQUIRED_MESSAGE;

  return (
    <Modal show={show} onHide={onHide} centered contentClassName="v3-dialog">
      <Modal.Header closeButton>
        <Modal.Title>{DIALOG_TITLE}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">{DIALOG_DESCRIPTION}</p>

        <div
          className="rounded px-3 py-2 mb-3 font-monospace small d-flex align-items-center justify-content-between gap-2"
          style={{ backgroundColor: '#f7f8f9', border: '1px solid #d4d9d9', color: '#383b3b' }}
        >
          <span>Reference ID: {correlationId}</span>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="email-reference-id-copy-tooltip">{justCopied ? 'Copied!' : 'Copy reference ID'}</Tooltip>}
          >
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              style={{ color: 'inherit' }}
              aria-label={justCopied ? 'Reference ID copied' : 'Copy reference ID'}
              onClick={handleCopyReferenceId}
            >
              {justCopied ? <CheckIcon className="ao-small-icon" /> : <CopyIcon />}
            </button>
          </OverlayTrigger>
        </div>

        <details className="mb-3" open>
          <summary className="small fw-semibold" style={{ cursor: 'pointer' }}>
            What we&apos;ll send
          </summary>
          <ul className="small mt-2 mb-2" style={{ color: '#383b3b' }}>
            {WHAT_WE_SEND_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p style={{ fontSize: '0.75rem', color: '#7b888a' }}>
            This does not include the content of documents, surveys, or search results you
            were viewing. See our{' '}
            <a
              href={PRIVACY_STATEMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#383b3b', fontWeight: 600 }}
            >
              privacy notice
            </a>{' '}
            for details.
          </p>
        </details>

        <Form.Group className="mb-3">
          <Form.Label>{COMMENT_LABEL}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={COMMENT_PLACEHOLDER}
          />
        </Form.Group>

        <Form.Check
          type="checkbox"
          id="email-error-consent"
          label={CONSENT_LABEL}
          checked={hasConsented}
          onChange={(event) => setHasConsented(event.target.checked)}
        />

        {!hasConsented && (
          <p className="small mb-0" style={{ fontSize: '0.75rem', color: '#7b888a' }}>
            Check the box above to enable sending.
          </p>
        )}

        {isCaptchaVisible && (
          <div className="mt-3">
            {REACT_APP_GOOGLE_RECAPTCHA_ENTERPRISE_SITE_KEY_FOR_ERROR_EMAIL ? (
              <div
                className="g-recaptcha"
                data-sitekey={REACT_APP_GOOGLE_RECAPTCHA_ENTERPRISE_SITE_KEY_FOR_ERROR_EMAIL}
                data-action={RECAPTCHA_ACTION}
              />
            ) : (
              <Alert variant="warning">
                reCAPTCHA Enterprise site key is not configured. Please set
                REACT_APP_GOOGLE_RECAPTCHA_ENTERPRISE_SITE_KEY_FOR_ERROR_EMAIL in your
                environment variables.
              </Alert>
            )}
          </div>
        )}

        {dialogError && (
          <Alert variant="danger" className="mt-3">
            {dialogError}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" className="ao-dialog-link me-auto px-0" onClick={openPrivacyNotice}>
          Privacy notice
        </Button>
        <Button
          variant="outline-secondary"
          className="ao-dialog-cancel-btn"
          disabled={isSending}
          onClick={onHide}
        >
          Cancel
        </Button>
        {sendTooltipMessage ? (
          <OverlayTrigger placement="top" overlay={<Tooltip id="email-error-send-tooltip">{sendTooltipMessage}</Tooltip>}>
            <span className="d-inline-block">
              <Button
                variant="dark"
                className="ao-dialog-send-btn"
                disabled={isSendDisabled}
                style={{ pointerEvents: 'none' }}
                onClick={handleSendClick}
              >
                Send details
              </Button>
            </span>
          </OverlayTrigger>
        ) : (
          <Button
            variant="dark"
            className="ao-dialog-send-btn"
            disabled={isSendDisabled}
            onClick={handleSendClick}
          >
            {isSending ? 'Sending...' : 'Send details'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
